import React from 'react';

/**
 * ElchmE Custom Icons
 * Beautiful, custom icons from the ElchmE.com ecosystem
 * Colors: Observe (#3B82F6 blue), Feel (#EF4444 coral), Need (#10B981 teal), Gold (#F59E0B amber)
 */

// ==================== OFNR CORE ICONS ====================

// Observe Icon - Blue Eye with radiating circles
export const ObserveIcon = ({ size = 24, className = '' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Observe"
  >
    <circle cx="24" cy="24" r="22" stroke="#3B82F6" strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    <circle cx="24" cy="24" r="20" stroke="#3B82F6" strokeWidth="0.3" strokeOpacity="0.2" fill="none" strokeDasharray="2 4" />
    <path 
      d="M4 24C4 24 12 10 24 10C36 10 44 24 44 24C44 24 36 38 24 38C12 38 4 24 4 24Z" 
      stroke="#3B82F6" 
      strokeWidth="1.5" 
      strokeOpacity="0.8" 
      fill="#3B82F6" 
      fillOpacity="0.05" 
    />
    <circle cx="24" cy="24" r="8" stroke="#3B82F6" strokeWidth="1" fill="#3B82F6" fillOpacity="0.2" />
    <circle cx="24" cy="24" r="4" fill="#3B82F6" fillOpacity="0.6" />
    <circle cx="22" cy="22" r="1.5" fill="white" fillOpacity="0.8" />
  </svg>
);

// Feel Icon - Coral Heart with pulsing circles
export const FeelIcon = ({ size = 24, className = '' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Feel"
  >
    <circle cx="24" cy="26" r="20" stroke="#EF4444" strokeWidth="0.3" strokeOpacity="0.15" fill="none" />
    <circle cx="24" cy="26" r="17" stroke="#EF4444" strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <circle cx="24" cy="26" r="14" stroke="#EF4444" strokeWidth="0.4" strokeOpacity="0.25" fill="none" />
    <path 
      d="M24 42L6 24C6 24 2 18 6 12C10 6 18 8 24 16C30 8 38 6 42 12C46 18 42 24 42 24L24 42Z" 
      stroke="#EF4444" 
      strokeWidth="1.5" 
      strokeOpacity="0.9" 
      fill="#EF4444" 
      fillOpacity="0.3" 
    />
    <path 
      d="M24 36L12 24C12 24 10 20 12 16C14 12 18 13 24 18C30 13 34 12 36 16C38 20 36 24 36 24L24 36Z" 
      fill="#EF4444" 
      fillOpacity="0.5" 
    />
  </svg>
);

// Need Icon - Teal Droplet/Seedling with growth circles
export const NeedIcon = ({ size = 24, className = '' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Need"
  >
    <circle cx="24" cy="24" r="22" stroke="#10B981" strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <circle cx="24" cy="24" r="8" stroke="#10B981" strokeWidth="0.8" strokeOpacity="0.4" fill="#10B981" fillOpacity="0.05" />
    <circle cx="24" cy="16" r="8" stroke="#10B981" strokeWidth="0.5" strokeOpacity="0.25" fill="none" />
    <circle cx="24" cy="32" r="8" stroke="#10B981" strokeWidth="0.5" strokeOpacity="0.25" fill="none" />
    <circle cx="17" cy="20" r="8" stroke="#10B981" strokeWidth="0.5" strokeOpacity="0.25" fill="none" />
    <circle cx="31" cy="20" r="8" stroke="#10B981" strokeWidth="0.5" strokeOpacity="0.25" fill="none" />
    <path 
      d="M24 8C24 8 14 22 14 30C14 38 24 42 24 42C24 42 34 38 34 30C34 22 24 8 24 8Z" 
      stroke="#10B981" 
      strokeWidth="1.5" 
      strokeOpacity="0.8" 
      fill="#10B981" 
      fillOpacity="0.3" 
    />
    <path 
      d="M24 16C24 16 18 26 18 32C18 36 24 38 24 38C24 38 30 36 30 32C30 26 24 16 24 16Z" 
      fill="#10B981" 
      fillOpacity="0.5" 
    />
  </svg>
);

// Gold Icon - Golden Star with radiating points
export const GoldIcon = ({ size = 24, className = '' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Gold"
  >
    <circle cx="24" cy="24" r="22" stroke="#F59E0B" strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    <circle cx="24" cy="24" r="20" stroke="#F59E0B" strokeWidth="0.3" strokeOpacity="0.2" fill="none" strokeDasharray="1 3" />
    <path 
      d="M24 2L27 18L44 14L30 24L44 34L27 30L24 46L21 30L4 34L18 24L4 14L21 18L24 2Z" 
      stroke="#F59E0B" 
      strokeWidth="1" 
      strokeOpacity="0.6" 
      fill="#F59E0B" 
      fillOpacity="0.1" 
      strokeLinejoin="round" 
    />
    <path 
      d="M24 12L32 24L24 36L16 24L24 12Z" 
      stroke="#F59E0B" 
      strokeWidth="1.5" 
      strokeOpacity="0.8" 
      fill="#F59E0B" 
      fillOpacity="0.4" 
    />
    <path 
      d="M24 18L28 24L24 30L20 24L24 18Z" 
      fill="#F59E0B" 
      fillOpacity="0.7" 
    />
  </svg>
);

// ==================== NAVIGATION ICONS ====================

// Home Icon - Golden House
export const HomeIcon = ({ size = 24, className = '', color = '#FBBF24' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Home"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    <path 
      d="M8 22L24 8L40 22V40H30V30H18V40H8V22Z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.3" 
      strokeLinejoin="round" 
    />
    <path 
      d="M18 30H30V40H18V30Z" 
      fill={color} 
      fillOpacity="0.2" 
    />
    <rect x="21" y="32" width="6" height="8" fill={color} fillOpacity="0.5" />
  </svg>
);

// Flow Icon - Wave/Connection
export const FlowIcon = ({ size = 24, className = '', color = '#94A3B8' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Flow"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path 
      d="M4 24C8 16 12 32 16 24C20 16 24 32 28 24C32 16 36 32 40 24C44 16 48 24 48 24" 
      stroke={color} 
      strokeWidth="2" 
      strokeOpacity="0.8" 
      fill="none" 
      strokeLinecap="round" 
    />
    <circle cx="16" cy="24" r="3" fill={color} fillOpacity="0.4" />
    <circle cx="32" cy="24" r="3" fill={color} fillOpacity="0.4" />
  </svg>
);

// Profile Icon - Person silhouette
export const ProfileIcon = ({ size = 24, className = '', color = '#94A3B8' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Profile"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    <circle cx="24" cy="24" r="18" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill={color} fillOpacity="0.03" strokeDasharray="3 2" />
    <circle cx="24" cy="18" r="5" stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.2" />
    <path 
      d="M16 38C16 30 20 26 24 26C28 26 32 30 32 38" 
      stroke={color} 
      strokeWidth="0.8" 
      fill={color} 
      fillOpacity="0.15" 
    />
  </svg>
);

// Sketch/Create Icon - Plus with sparkle
export const SketchIcon = ({ size = 24, className = '', color = '#94A3B8' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Create"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path 
      d="M24 12V36M12 24H36" 
      stroke={color} 
      strokeWidth="2" 
      strokeOpacity="0.8" 
      strokeLinecap="round" 
    />
    <circle cx="14" cy="14" r="2" fill={color} fillOpacity="0.5" />
    <circle cx="34" cy="14" r="1.5" fill={color} fillOpacity="0.4" />
    <circle cx="34" cy="34" r="2" fill={color} fillOpacity="0.5" />
  </svg>
);

// ==================== CHAPTER & CONTENT ICONS ====================

// Book/Chapter Icon
export const ChapterIcon = ({ size = 24, className = '', color = '#A855F7' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Chapter"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    <path 
      d="M10 8V40C10 40 16 36 24 36C32 36 38 40 38 40V8C38 8 32 12 24 12C16 12 10 8 10 8Z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.1" 
    />
    <path d="M24 12V36" stroke={color} strokeWidth="1" strokeOpacity="0.5" />
    <path d="M16 18H22" stroke={color} strokeWidth="1" strokeOpacity="0.6" />
    <path d="M16 24H22" stroke={color} strokeWidth="1" strokeOpacity="0.6" />
    <path d="M26 18H32" stroke={color} strokeWidth="1" strokeOpacity="0.6" />
    <path d="M26 24H32" stroke={color} strokeWidth="1" strokeOpacity="0.6" />
  </svg>
);

// Exercise/Practice Icon
export const ExerciseIcon = ({ size = 24, className = '', color = '#3B82F6' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Exercise"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path 
      d="M12 12L20 20M28 28L36 36" 
      stroke={color} 
      strokeWidth="2" 
      strokeOpacity="0.8" 
      strokeLinecap="round" 
    />
    <path 
      d="M36 12L28 20M20 28L12 36" 
      stroke={color} 
      strokeWidth="2" 
      strokeOpacity="0.8" 
      strokeLinecap="round" 
    />
    <circle cx="24" cy="24" r="6" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.2" />
    <circle cx="24" cy="24" r="2" fill={color} fillOpacity="0.6" />
  </svg>
);

// Homework/Assignment Icon
export const HomeworkIcon = ({ size = 24, className = '', color = '#8B5CF6' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Homework"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    <rect x="12" y="6" width="24" height="36" rx="2" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.1" />
    <path d="M18 14H30" stroke={color} strokeWidth="1" strokeOpacity="0.6" />
    <path d="M18 20H30" stroke={color} strokeWidth="1" strokeOpacity="0.6" />
    <path d="M18 26H26" stroke={color} strokeWidth="1" strokeOpacity="0.6" />
    <path 
      d="M16 32L20 36L32 24" 
      stroke={color} 
      strokeWidth="2" 
      strokeOpacity="0.8" 
      fill="none" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
    />
  </svg>
);

// Partner/Connection Icon
export const PartnerIcon = ({ size = 24, className = '', color = '#10B981' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Partner"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <circle cx="16" cy="18" r="5" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.2" />
    <circle cx="32" cy="18" r="5" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.2" />
    <path 
      d="M8 38C8 30 12 26 16 26C20 26 22 28 24 28C26 28 28 26 32 26C36 26 40 30 40 38" 
      stroke={color} 
      strokeWidth="1" 
      fill={color} 
      fillOpacity="0.15" 
    />
    <path d="M20 22C22 26 26 26 28 22" stroke={color} strokeWidth="1.5" strokeOpacity="0.6" fill="none" />
  </svg>
);

// ==================== EMOTION ICONS ====================

// Alarm/Warning Icon - For Alarm Feelings
export const AlarmIcon = ({ size = 24, className = '' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Alarm"
  >
    <circle cx="24" cy="24" r="22" stroke="#F59E0B" strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    <circle cx="24" cy="24" r="18" stroke="#F59E0B" strokeWidth="0.3" strokeOpacity="0.4" fill="#F59E0B" fillOpacity="0.05" />
    <path 
      d="M24 8L42 40H6L24 8Z" 
      stroke="#F59E0B" 
      strokeWidth="1.5" 
      fill="#F59E0B" 
      fillOpacity="0.2" 
      strokeLinejoin="round" 
    />
    <path d="M24 18V28" stroke="#F59E0B" strokeWidth="2.5" strokeOpacity="0.9" strokeLinecap="round" />
    <circle cx="24" cy="34" r="2" fill="#F59E0B" fillOpacity="0.9" />
  </svg>
);

// Anger Icon - Flame
export const AngerIcon = ({ size = 24, className = '' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Anger"
  >
    <circle cx="24" cy="24" r="22" stroke="#EF4444" strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path 
      d="M24 4C24 4 34 14 34 26C34 36 28 44 24 44C20 44 14 36 14 26C14 14 24 4 24 4Z" 
      stroke="#EF4444" 
      strokeWidth="1.5" 
      fill="#EF4444" 
      fillOpacity="0.3" 
    />
    <path 
      d="M24 16C24 16 30 22 30 30C30 36 26 40 24 40C22 40 18 36 18 30C18 22 24 16 24 16Z" 
      fill="#EF4444" 
      fillOpacity="0.5" 
    />
    <path 
      d="M24 26C24 26 27 29 27 33C27 36 25 38 24 38C23 38 21 36 21 33C21 29 24 26 24 26Z" 
      fill="#F59E0B" 
      fillOpacity="0.8" 
    />
  </svg>
);

// Guilt & Shame Icon - Burdened heart with weight
export const GuiltShameIcon = ({ size = 24, className = '' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Guilt and Shame"
  >
    <circle cx="24" cy="24" r="22" stroke="#A855F7" strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <circle cx="24" cy="24" r="18" stroke="#A855F7" strokeWidth="0.3" strokeOpacity="0.15" fill="none" />
    <path 
      d="M24 38L10 24C10 24 7 19 10 14C13 9 19 10 24 16C29 10 35 9 38 14C41 19 38 24 38 24L24 38Z" 
      stroke="#A855F7" 
      strokeWidth="1.5" 
      strokeOpacity="0.8" 
      fill="#A855F7" 
      fillOpacity="0.25" 
    />
    <path 
      d="M24 32L16 24C16 24 14 21 16 18C18 15 21 16 24 20C27 16 30 15 32 18C34 21 32 24 32 24L24 32Z" 
      fill="#A855F7" 
      fillOpacity="0.4" 
    />
    <path d="M18 8L24 14L30 8" stroke="#A855F7" strokeWidth="1.5" strokeOpacity="0.6" fill="none" strokeLinecap="round" />
    <circle cx="24" cy="6" r="2" fill="#A855F7" fillOpacity="0.5" />
  </svg>
);

// Sadness/Mourning Icon - Tear drop
export const SadnessIcon = ({ size = 24, className = '' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Sadness"
  >
    <circle cx="24" cy="24" r="22" stroke="#6366F1" strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path 
      d="M24 4C24 4 10 24 10 32C10 40 16 44 24 44C32 44 38 40 38 32C38 24 24 4 24 4Z" 
      stroke="#6366F1" 
      strokeWidth="1.5" 
      fill="#6366F1" 
      fillOpacity="0.3" 
    />
    <path 
      d="M24 14C24 14 16 28 16 34C16 38 20 40 24 40C28 40 32 38 32 34C32 28 24 14 24 14Z" 
      fill="#6366F1" 
      fillOpacity="0.5" 
    />
    <ellipse cx="20" cy="30" rx="2" ry="3" fill="white" fillOpacity="0.4" />
  </svg>
);

// Peace/Calm Icon - Lotus/Serene
export const PeaceIcon = ({ size = 24, className = '' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Peace"
  >
    <circle cx="24" cy="24" r="22" stroke="#14B8A6" strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <ellipse cx="24" cy="34" rx="12" ry="6" stroke="#14B8A6" strokeWidth="1" fill="#14B8A6" fillOpacity="0.2" />
    <path 
      d="M24 32C24 32 18 26 18 20C18 14 24 8 24 8C24 8 30 14 30 20C30 26 24 32 24 32Z" 
      stroke="#14B8A6" 
      strokeWidth="1.5" 
      fill="#14B8A6" 
      fillOpacity="0.3" 
    />
    <path 
      d="M16 30C16 30 10 26 8 22C6 18 10 16 14 18C18 20 16 30 16 30Z" 
      stroke="#14B8A6" 
      strokeWidth="1" 
      fill="#14B8A6" 
      fillOpacity="0.25" 
    />
    <path 
      d="M32 30C32 30 38 26 40 22C42 18 38 16 34 18C30 20 32 30 32 30Z" 
      stroke="#14B8A6" 
      strokeWidth="1" 
      fill="#14B8A6" 
      fillOpacity="0.25" 
    />
  </svg>
);

// ==================== ACTION ICONS ====================

// Video/Watch Icon
export const VideoIcon = ({ size = 24, className = '', color = '#A855F7' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Video"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    <circle cx="24" cy="24" r="19" stroke={color} strokeWidth="1" strokeOpacity="0.4" fill={color} fillOpacity="0.05" />
    <path 
      d="M18 12L38 24L18 36V12Z" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeOpacity="0.8" 
      fill={color} 
      fillOpacity="0.3" 
      strokeLinejoin="round" 
    />
    <path d="M22 18L32 24L22 30V18Z" fill={color} fillOpacity="0.5" />
  </svg>
);

// Reading/Book Open Icon
export const ReadingIcon = ({ size = 24, className = '', color = '#3B82F6' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Reading"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path 
      d="M6 12C6 12 12 16 24 16C36 16 42 12 42 12V40C42 40 36 36 24 36C12 36 6 40 6 40V12Z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.1" 
    />
    <path d="M24 16V36" stroke={color} strokeWidth="1" strokeOpacity="0.5" />
    <path d="M12 22H20" stroke={color} strokeWidth="0.8" strokeOpacity="0.5" />
    <path d="M12 28H18" stroke={color} strokeWidth="0.8" strokeOpacity="0.5" />
    <path d="M28 22H36" stroke={color} strokeWidth="0.8" strokeOpacity="0.5" />
    <path d="M28 28H34" stroke={color} strokeWidth="0.8" strokeOpacity="0.5" />
  </svg>
);

// Save/Progress Icon
export const SaveIcon = ({ size = 24, className = '', color = '#10B981' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Save"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    <path 
      d="M12 8H32L40 16V40H8V8H12Z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.1" 
    />
    <rect x="16" y="8" width="16" height="12" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.2" />
    <rect x="14" y="28" width="20" height="12" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.2" />
  </svg>
);

// ==================== UI ICONS ====================

// Arrow/Navigation Icons
export const ArrowRightIcon = ({ size = 24, className = '', color = 'currentColor' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Next"
  >
    <path 
      d="M18 8L34 24L18 40" 
      stroke={color} 
      strokeWidth="2" 
      strokeOpacity="0.8" 
      fill="none" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
    />
    <path 
      d="M22 16L30 24L22 32" 
      stroke={color} 
      strokeWidth="1" 
      strokeOpacity="0.4" 
      fill="none" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
    />
  </svg>
);

export const ArrowLeftIcon = ({ size = 24, className = '', color = 'currentColor' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Previous"
  >
    <path 
      d="M30 8L14 24L30 40" 
      stroke={color} 
      strokeWidth="2" 
      strokeOpacity="0.8" 
      fill="none" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
    />
    <path 
      d="M26 16L18 24L26 32" 
      stroke={color} 
      strokeWidth="1" 
      strokeOpacity="0.4" 
      fill="none" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
    />
  </svg>
);

export const ArrowDownIcon = ({ size = 24, className = '', color = '#FBBF24' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Down"
  >
    <path 
      d="M12 18L24 30L36 18" 
      stroke={color} 
      strokeWidth="2.5" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
    />
  </svg>
);

// Check/Success Icon
export const CheckIcon = ({ size = 24, className = '', color = '#10B981' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Check"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    <circle cx="24" cy="24" r="18" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.1" />
    <path 
      d="M14 24L22 32L36 16" 
      stroke={color} 
      strokeWidth="3" 
      strokeOpacity="0.9" 
      fill="none" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
    />
  </svg>
);

// Star/Featured Icon
export const StarIcon = ({ size = 24, className = '', color = '#F59E0B' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Featured"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path 
      d="M24 6L28 18H42L31 26L35 40L24 31L13 40L17 26L6 18H20L24 6Z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.4" 
      strokeLinejoin="round" 
    />
  </svg>
);

// Lightbulb/Insight Icon
export const InsightIcon = ({ size = 24, className = '', color = '#F59E0B' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Insight"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path 
      d="M18 36H30V40C30 42 28 44 24 44C20 44 18 42 18 40V36Z" 
      stroke={color} 
      strokeWidth="1" 
      fill={color} 
      fillOpacity="0.3" 
    />
    <path 
      d="M16 24C16 16 20 10 24 10C28 10 32 16 32 24C32 28 30 32 28 34H20C18 32 16 28 16 24Z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.2" 
    />
    <path d="M20 34H28" stroke={color} strokeWidth="1" />
    <path d="M24 4V8" stroke={color} strokeWidth="1.5" strokeOpacity="0.6" strokeLinecap="round" />
    <path d="M36 12L33 15" stroke={color} strokeWidth="1.5" strokeOpacity="0.6" strokeLinecap="round" />
    <path d="M12 12L15 15" stroke={color} strokeWidth="1.5" strokeOpacity="0.6" strokeLinecap="round" />
    <path d="M40 24H36" stroke={color} strokeWidth="1.5" strokeOpacity="0.6" strokeLinecap="round" />
    <path d="M12 24H8" stroke={color} strokeWidth="1.5" strokeOpacity="0.6" strokeLinecap="round" />
  </svg>
);

// Brain/Thought Icon
export const ThoughtIcon = ({ size = 24, className = '', color = '#8B5CF6' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Thought"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <circle cx="16" cy="20" r="8" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.15" />
    <circle cx="32" cy="20" r="8" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.15" />
    <circle cx="20" cy="30" r="6" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.15" />
    <circle cx="28" cy="30" r="6" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.15" />
    <ellipse cx="24" cy="38" rx="10" ry="4" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.2" />
    <path d="M20 16C22 14 26 14 28 16" stroke={color} strokeWidth="1" strokeOpacity="0.6" fill="none" />
  </svg>
);

// Timer/Clock Icon
export const TimerIcon = ({ size = 24, className = '', color = '#F59E0B' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Timer"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <circle cx="24" cy="26" r="16" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.15" />
    <path d="M20 6H28" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M24 6V10" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M24 16V26L30 30" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="24" cy="26" r="2" fill={color} fillOpacity="0.5" />
    <path d="M36 14L38 12" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// ==================== ADDITIONAL ICONS ====================

// Gift/Offering Icon
export const GiftIcon = ({ size = 24, className = '', color = '#F59E0B' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Gift"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <rect x="8" y="18" width="32" height="8" rx="2" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.2" />
    <rect x="10" y="26" width="28" height="16" rx="1" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.15" />
    <path d="M24 18V42" stroke={color} strokeWidth="1.5" strokeOpacity="0.6" />
    <path 
      d="M24 18C24 18 20 14 16 14C12 14 12 18 16 18C20 18 24 18 24 18Z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.3" 
    />
    <path 
      d="M24 18C24 18 28 14 32 14C36 14 36 18 32 18C28 18 24 18 24 18Z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.3" 
    />
  </svg>
);

// Message/Request Icon
export const RequestIcon = ({ size = 24, className = '', color = '#A855F7' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Request"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path 
      d="M8 14H40V34C40 36 38 38 36 38H18L10 44V38H8V14Z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.15" 
    />
    <path d="M14 22H34" stroke={color} strokeWidth="1.5" strokeOpacity="0.6" strokeLinecap="round" />
    <path d="M14 28H28" stroke={color} strokeWidth="1.5" strokeOpacity="0.6" strokeLinecap="round" />
    <circle cx="36" cy="12" r="6" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.4" />
    <path d="M36 9V15M33 12H39" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// Magic/Sparkle Icon
export const SparkleIcon = ({ size = 24, className = '', color = '#EC4899' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Sparkle"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path 
      d="M24 4L27 18L42 16L30 24L42 32L27 30L24 44L21 30L6 32L18 24L6 16L21 18L24 4Z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.3" 
      strokeLinejoin="round" 
    />
    <circle cx="24" cy="24" r="4" fill={color} fillOpacity="0.6" />
    <circle cx="12" cy="12" r="2" fill={color} fillOpacity="0.4" />
    <circle cx="36" cy="12" r="1.5" fill={color} fillOpacity="0.3" />
    <circle cx="36" cy="36" r="2" fill={color} fillOpacity="0.4" />
    <circle cx="12" cy="36" r="1.5" fill={color} fillOpacity="0.3" />
  </svg>
);

// Fear/Shield Icon
export const FearIcon = ({ size = 24, className = '', color = '#EF4444' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Fear"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path 
      d="M24 4C24 4 8 10 8 24C8 38 24 44 24 44C24 44 40 38 40 24C40 10 24 4 24 4Z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.15" 
    />
    <path d="M24 14V28" stroke={color} strokeWidth="2.5" strokeOpacity="0.8" strokeLinecap="round" />
    <circle cx="24" cy="34" r="2.5" fill={color} fillOpacity="0.8" />
  </svg>
);

// Ideas/List Icon
export const IdeasIcon = ({ size = 24, className = '', color = '#F59E0B' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Ideas"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <rect x="10" y="8" width="28" height="32" rx="2" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.1" />
    <circle cx="16" cy="16" r="2" fill={color} fillOpacity="0.6" />
    <path d="M22 16H32" stroke={color} strokeWidth="1.5" strokeOpacity="0.6" strokeLinecap="round" />
    <circle cx="16" cy="24" r="2" fill={color} fillOpacity="0.6" />
    <path d="M22 24H32" stroke={color} strokeWidth="1.5" strokeOpacity="0.6" strokeLinecap="round" />
    <circle cx="16" cy="32" r="2" fill={color} fillOpacity="0.6" />
    <path d="M22 32H32" stroke={color} strokeWidth="1.5" strokeOpacity="0.6" strokeLinecap="round" />
  </svg>
);

// Expand/Fullscreen Icon
export const ExpandIcon = ({ size = 24, className = '', color = 'currentColor' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Expand"
  >
    <path d="M8 18V8H18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M40 18V8H30" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 30V40H18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M40 30V40H30" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 8L18 18" stroke={color} strokeWidth="1.5" strokeOpacity="0.5" strokeLinecap="round" />
    <path d="M40 8L30 18" stroke={color} strokeWidth="1.5" strokeOpacity="0.5" strokeLinecap="round" />
    <path d="M8 40L18 30" stroke={color} strokeWidth="1.5" strokeOpacity="0.5" strokeLinecap="round" />
    <path d="M40 40L30 30" stroke={color} strokeWidth="1.5" strokeOpacity="0.5" strokeLinecap="round" />
  </svg>
);

// Collapse/Minimize Icon
export const CollapseIcon = ({ size = 24, className = '', color = 'currentColor' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Collapse"
  >
    <path d="M18 8V18H8" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M30 8V18H40" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18 40V30H8" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M30 40V30H40" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Reset/Refresh Icon
export const ResetIcon = ({ size = 24, className = '', color = 'currentColor' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Reset"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path 
      d="M12 24C12 17.4 17.4 12 24 12C30.6 12 36 17.4 36 24C36 30.6 30.6 36 24 36" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round" 
      fill="none" 
    />
    <path d="M8 20L12 24L16 20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <path d="M20 36L24 40L28 36" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

// Download/PDF Icon
export const DownloadIcon = ({ size = 24, className = '', color = 'currentColor' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Download"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path d="M24 8V30" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M16 24L24 32L32 24" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <path d="M10 38H38" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Cloud/Sync Icon
export const CloudIcon = ({ size = 24, className = '', color = '#3B82F6' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Cloud"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path 
      d="M12 32C8 32 4 28 4 24C4 20 8 16 12 16C12 12 16 8 22 8C28 8 32 12 34 16C38 16 44 20 44 26C44 32 40 36 34 36H12Z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.2" 
    />
    <path d="M24 22V32" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M20 28L24 32L28 28" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Cloud Off Icon
export const CloudOffIcon = ({ size = 24, className = '', color = '#94A3B8' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Cloud Off"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path 
      d="M12 32C8 32 4 28 4 24C4 20 8 16 12 16C12 12 16 8 22 8C28 8 32 12 34 16C38 16 44 20 44 26C44 32 40 36 34 36H12Z" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeOpacity="0.5"
      fill={color} 
      fillOpacity="0.1" 
    />
    <path d="M8 8L40 40" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Eye/View Icon
export const ViewIcon = ({ size = 24, className = '', color = '#A855F7' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="View"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path 
      d="M4 24C4 24 12 10 24 10C36 10 44 24 44 24C44 24 36 38 24 38C12 38 4 24 4 24Z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.1" 
    />
    <circle cx="24" cy="24" r="8" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.2" />
    <circle cx="24" cy="24" r="4" fill={color} fillOpacity="0.5" />
  </svg>
);

// Eye Off/Hide Icon
export const HideIcon = ({ size = 24, className = '', color = '#94A3B8' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Hide"
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path 
      d="M4 24C4 24 12 10 24 10C36 10 44 24 44 24C44 24 36 38 24 38C12 38 4 24 4 24Z" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeOpacity="0.5"
      fill={color} 
      fillOpacity="0.05" 
    />
    <path d="M8 8L40 40" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Wheel/Circle Icon (for Communication Wheel)
export const WheelIcon = ({ size = 24, className = '', color = '#10B981' }) => (
  <svg 
    viewBox="0 0 48 48" 
    width={size} 
    height={size} 
    className={className}
    aria-label="Wheel"
  >
    <circle cx="24" cy="24" r="20" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.1" />
    <circle cx="24" cy="24" r="14" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.15" />
    <circle cx="24" cy="24" r="8" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.2" />
    <circle cx="24" cy="24" r="3" fill={color} fillOpacity="0.5" />
    <path d="M24 4V12" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M24 36V44" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M4 24H12" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M36 24H44" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M10 10L16 16" stroke={color} strokeWidth="1" strokeLinecap="round" />
    <path d="M32 32L38 38" stroke={color} strokeWidth="1" strokeLinecap="round" />
    <path d="M38 10L32 16" stroke={color} strokeWidth="1" strokeLinecap="round" />
    <path d="M16 32L10 38" stroke={color} strokeWidth="1" strokeLinecap="round" />
  </svg>
);

// ==================== EXPORT ALL ====================

export const ElchmeIcons = {
  // OFNR
  Observe: ObserveIcon,
  Feel: FeelIcon,
  Need: NeedIcon,
  Gold: GoldIcon,
  
  // Navigation
  Home: HomeIcon,
  Flow: FlowIcon,
  Profile: ProfileIcon,
  Sketch: SketchIcon,
  
  // Chapter/Content
  Chapter: ChapterIcon,
  Exercise: ExerciseIcon,
  Homework: HomeworkIcon,
  Partner: PartnerIcon,
  
  // Emotions
  Alarm: AlarmIcon,
  Anger: AngerIcon,
  GuiltShame: GuiltShameIcon,
  Sadness: SadnessIcon,
  Peace: PeaceIcon,
  Fear: FearIcon,
  
  // Actions
  Video: VideoIcon,
  Reading: ReadingIcon,
  Save: SaveIcon,
  Gift: GiftIcon,
  Request: RequestIcon,
  Sparkle: SparkleIcon,
  Ideas: IdeasIcon,
  Download: DownloadIcon,
  Timer: TimerIcon,
  
  // UI
  ArrowRight: ArrowRightIcon,
  ArrowLeft: ArrowLeftIcon,
  ArrowDown: ArrowDownIcon,
  Check: CheckIcon,
  Star: StarIcon,
  Insight: InsightIcon,
  Thought: ThoughtIcon,
  Expand: ExpandIcon,
  Collapse: CollapseIcon,
  Reset: ResetIcon,
  Cloud: CloudIcon,
  CloudOff: CloudOffIcon,
  View: ViewIcon,
  Hide: HideIcon,
  Wheel: WheelIcon,
};

export default ElchmeIcons;
